import { type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/shared/lib/utils";

import { colocaSurfaceVariants } from "./colocaSurfaceVariants";

export interface ColocaSurfaceProps
  extends React.ComponentPropsWithoutRef<"div">, VariantProps<typeof colocaSurfaceVariants> {}

/**
 * The card surface every dashboard panel sits on: rounded corners, a hairline border, the card background and
 * a resting shadow. It exists so the string is written once instead of being retyped in each module — the
 * repo had more than thirty hand-written copies of it, disagreeing on the radius.
 *
 * `variant` picks the background: `panel` for content, `muted` for a de-emphasised band (a footer, a nested
 * summary), `plain` when the surface sits inside another card and a second shadow would look like a seam.
 *
 * Use `interactive` for rows and tiles that navigate somewhere. For a non-`div` root — a `<section>` hero, an
 * `<article>` card, a `<button>` row — apply `colocaSurfaceVariants(...)` from the sibling module instead.
 *
 * @summary the standard card surface (border + background + radius + shadow)
 */
export const ColocaSurface = React.forwardRef<HTMLDivElement, ColocaSurfaceProps>(function ColocaSurface(
  { variant, padding, interactive, className, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(colocaSurfaceVariants({ variant, padding, interactive }), className)}
      {...rest}
    />
  );
});
